import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Pressable, Text, View } from 'react-native';
import { FadeIn, FadeOut } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AnimatedView } from '@/components/ui/animated';
import { Toast, ToastType, useToastStore } from '@/store/toastStore';

type IoniconName = React.ComponentProps<typeof Ionicons>['name'];

const toastStyles = (type: ToastType): { icon: IoniconName; className: string } => {
  switch (type) {
    case 'success':
      return { icon: 'checkmark-circle', className: 'bg-green-600' };
    case 'error':
      return { icon: 'close-circle', className: 'bg-red-600' };
    case 'warning':
      return { icon: 'warning', className: 'bg-yellow-600' };
    case 'info':
    default:
      return { icon: 'information-circle', className: 'bg-blue-600' };
  }
};

const ToastItem: React.FC<{ toast: Toast }> = ({ toast }) => {
  const { removeToast } = useToastStore();
  const { icon, className } = toastStyles(toast.type);

  return (
    <AnimatedView
      entering={FadeIn.duration(300)}
      exiting={FadeOut.duration(300)}
      className={`my-1 w-full max-w-sm flex-row items-center rounded-lg p-3 shadow-md ${className}`}
    >
      <Ionicons name={icon} size={20} color="white" className="mr-3" />
      <Text className="mr-4 flex-1 font-semibold text-sm text-white">{toast.message}</Text>
      <Pressable onPress={() => removeToast(toast.id)}>
        <Ionicons name="close" size={18} color="white" />
      </Pressable>
    </AnimatedView>
  );
};

export const ToastContainer: React.FC = () => {
  const { toasts } = useToastStore();
  const insets = useSafeAreaInsets();
  const topOffset = insets.top || 10;

  if (toasts.length === 0) {
    return null;
  }

  return (
    <View
      style={{ paddingTop: topOffset }}
      className="pointer-events-box-none absolute left-0 right-0 top-0 z-[100] items-center"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </View>
  );
};
